import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button, Row, Col, Card, Space, Tag } from 'antd';
import { Link } from 'react-router-dom';
import { RocketOutlined, RobotOutlined, FileTextOutlined, TeamOutlined, SendOutlined } from '@ant-design/icons';
import SEO from '../../components/SEO';
import OrganizationSchema from '../../components/SEO/OrganizationSchema';
import './HomePage.css';

const { Title, Paragraph } = Typography;

const HomePage: React.FC = () => {
  const { t } = useTranslation('landing');

  const features = [
    {
      icon: <RobotOutlined />,
      title: t('landing.features.ai.title'),
      description: t('landing.features.ai.description'),
    },
    {
      icon: <FileTextOutlined />,
      title: t('landing.features.documentation.title'),
      description: t('landing.features.documentation.description'),
    },
    {
      icon: <RocketOutlined />,
      title: t('landing.features.automation.title'),
      description: t('landing.features.automation.description'),
    },
    {
      icon: <TeamOutlined />,
      title: t('landing.features.collaboration.title'),
      description: t('landing.features.collaboration.description'),
    },
  ];

  return (
    <>
      <SEO
        title="QATE - AI-powered платформа управления тестированием | Quality Assurance Test Engineer"
        description="Революционная платформа QATE для управления тестированием с поддержкой ИИ. Автоматизируйте тест-кейсы, управляйте багами, улучшайте качество ПО. Альфа-тестирование доступно!"
        keywords="QATE, test management, QA platform, quality assurance, AI testing, тест менеджмент, QA платформа, автоматизация тестирования, управление качеством, тест кейсы"
        canonicalUrl="/"
        ogTitle="QATE - AI-powered платформа управления тестированием"
        ogDescription="Революционная платформа QATE для управления тестированием с поддержкой ИИ. Автоматизируйте тест-кейсы, управляйте багами, улучшайте качество ПО."
        schema={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "QATE",
          "alternateName": "Quality Assurance Test Engineer",
          "description": "AI-powered платформа управления тестированием для автоматизации QA процессов",
          "url": "https://qate.ru",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "RUB",
            "availability": "https://schema.org/InStock"
          },
          "provider": {
            "@type": "Organization",
            "name": "QATE",
            "url": "https://qate.ru"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "150"
          }
        }}
      />
      <OrganizationSchema />
      <div className="home-page">
        <div className="home-container">
          <Row justify="center" align="middle" className="hero-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <div className="alpha-tag">
                <Tag color="blue">
                  {t('landing.hero.alpha')}
                </Tag>
              </div>
              <Title level={1} className="hero-title">{t('landing.hero.title')}</Title>
              <Paragraph className="hero-subtitle">
                {t('landing.hero.subtitle')}
              </Paragraph>
              <Paragraph type="secondary" className="hero-description">
                {t('landing.hero.alphaDescription')}
              </Paragraph>
              <Space size="middle" className="hero-buttons">
                <Link to="/register">
                  <Button type="primary" size="large" className="hero-button primary">
                    {t('landing.hero.register')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="large" className="hero-button secondary">
                    {t('landing.hero.login')}
                  </Button>
                </Link>
                <a href="https://t.me/QAappbot" target="_blank" rel="noopener noreferrer">
                  <Button size="large" className="hero-button telegram">
                    <SendOutlined /> Telegram
                  </Button>
                </a>
              </Space>
            </Col>
          </Row>

          <Row gutter={[32, 32]} className="features-section">
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card hoverable className="feature-card">
                  <div className="feature-content">
                    <div className="feature-icon">{feature.icon}</div>
                    <Title level={4} className="feature-title">{feature.title}</Title>
                    <Paragraph className="feature-description">{feature.description}</Paragraph>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Row justify="center" className="alpha-section">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <Title level={2}>{t('landing.alpha.title')}</Title>
              <Paragraph style={{ fontSize: '16px' }}>
                {t('landing.alpha.description')}
              </Paragraph>
            </Col>
          </Row>

          {/* CTA Section */}
          <Row justify="center" className="home-cta">
            <Col xs={24} md={16} lg={12} style={{ textAlign: 'center' }}>
              <Title level={2}>{t('landing.cta.title')}</Title>
              <Paragraph className="cta-subtitle">
                {t('landing.cta.subtitle')}
              </Paragraph>
              <Space size="middle" className="cta-buttons">
                <Link to="/register">
                  <Button 
                    type="primary" 
                    size="large"
                    className="cta-button primary-button"
                    icon={<RocketOutlined />}
                  >
                    {t('landing.cta.button')}
                  </Button>
                </Link>
                <a href="https://t.me/QAappbot" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="large"
                    className="cta-button secondary-button"
                    icon={<SendOutlined />}
                  >
                    Telegram
                  </Button>
                </a>
              </Space>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default HomePage; 